import {
  CloseOutlined,
  EditOutlined,
  InfoCircleOutlined,
  SaveOutlined
} from "@ant-design/icons";
import { Button, Divider, Input, Popover, Select, Tag, message } from "antd";
import { useContext, useEffect, useMemo, useState } from "react";
import { updateProjectIntegration } from "services/integration-service";
import {
  isPermissionNotGrantted,
  ProjectContext,
  TProjectContext
} from "context/ProjectProvider";
import { ErrorMessages, ProjectPermissionEnum } from "../../../../constants";
import { ProjectIntegrationsTabSharedContext } from "../project-integrations-tab-context";

export default function ProjectProcoreIntegrationDetails() {
  const { tokenRetrievalState, tokenContents }: TProjectContext =
    useContext(ProjectContext);

  const { sharedState, dispatch } = useContext(
    ProjectIntegrationsTabSharedContext
  );

  const canChangeIntegration = !isPermissionNotGrantted(
    ProjectPermissionEnum.changeProjectIntegration,
    tokenContents?.role!
  );

  const [isIntegrationNameInEditMode, setIsIntegrationNameInEditMode] =
    useState(false);
  const [isEmailInEditMode, setIsEmailInEditMode] = useState(false);
  const [alertEmails, setAlertEmails] = useState([]);

  const [saveIntgNameInProg, setSaveIntgNameInProg] = useState(false);
  const [savingEmailInProg, setSavingEmailInProg] = useState(false);
  const [integrationName, setIntegrationName] = useState("");

  const integrationDetails = useMemo(() => {
    return sharedState.projectIntegrationConfigs.find((config: any) => {
      return config.id === sharedState.additionalData.id;
    });
  }, [sharedState.additionalData.id, sharedState.projectIntegrationConfigs]);

  const integrationProperties = integrationDetails.properties;
  const subscriptionIntegrationProperties =
    integrationDetails.subscriptionProperties;

  const incomingAlertEmails = useMemo(() => {
    return integrationProperties.PROJECT_ALERT_EMAIL.split(",");
  }, [integrationProperties.PROJECT_ALERT_EMAIL]);

  useEffect(() => {
    setAlertEmails(incomingAlertEmails);
  }, [incomingAlertEmails]);

  useEffect(() => {
    setIntegrationName(integrationProperties?.INTEGRATION_NAME);
  }, [integrationProperties?.INTEGRATION_NAME]);

  const saveField = async (
    payloadData: any,
    messages: { success: string; error: string }
  ) => {
    const payload: any = [
      {
        ...payloadData
      }
    ];

    const updateResponse = await updateProjectIntegration(
      sharedState.system,
      integrationDetails.id,
      payload,
      tokenRetrievalState.token
    );

    if (updateResponse.success) {
      message.success(messages.success);
      setIsEmailInEditMode(false);
    } else {
      message.error(messages.error);
    }
    return updateResponse;
  };

  const resetEmailFieldState = () => {
    setIsEmailInEditMode(false);
    setAlertEmails(incomingAlertEmails);
  };

  const resetIntgNameFieldState = () => {
    setIsIntegrationNameInEditMode(false);
    setIntegrationName(integrationProperties.INTEGRATION_NAME);
  };

  const saveEmail = async () => {
    setSavingEmailInProg(true);
    await saveField(
      {
        properties: {
          project_alert_email: alertEmails.join(",")
        }
      },
      {
        success: "Notification recipients updated successfully.",
        error: "Failed to update notification recipients."
      }
    );
    setSavingEmailInProg(false);
  };

  const saveIntegName = async () => {
    setSaveIntgNameInProg(true);
    await saveField(
      {
        properties: {
          integration_name: integrationName
        }
      },
      {
        success: "Integration name updated successfully.",
        error: "Failed to update integration name."
      }
    );
    setSaveIntgNameInProg(false);
    resetIntgNameFieldState();
  };

  const updateInProgress = savingEmailInProg || saveIntgNameInProg;

  return (
    <div className="h-full flex flex-col">
      <div className="h-full relative">
        <div className="grow w-full md:max-w-screen-sm mx-auto py-4">
          <div className="mt-8 w-[500px] space-y-5">
            <div>
              <div className="flex">
                <span className="form-field-heading-2">Integration Name</span>
              </div>
              {isIntegrationNameInEditMode && (
                <div className="w-full flex items-start space-x-2">
                  <div className="w-full">
                    <Input
                      className="normal-case w-full"
                      disabled={saveIntgNameInProg}
                      value={integrationName}
                      onChange={(event: any) => {
                        setIntegrationName(event.target.value);
                      }}
                      data-testid="integration-name-input"
                    />
                    {!integrationName.trim() && (
                      <div className="text-ciq-error-500">
                        Please enter the integration name
                      </div>
                    )}
                  </div>
                  <Button
                    size="small"
                    className="bg-ciq-neutral-25 p-2 mt-1"
                    title="Save"
                    icon={<SaveOutlined />}
                    onClick={async () => {
                      saveIntegName();
                    }}
                    disabled={!integrationName.trim() || updateInProgress}
                    loading={saveIntgNameInProg}
                    data-testid="save-integration-name-button"
                  />
                  <Button
                    size="small"
                    className="bg-ciq-neutral-25 mt-1"
                    title="Cancel"
                    onClick={resetIntgNameFieldState}
                    icon={<CloseOutlined />}
                    disabled={saveIntgNameInProg}
                    data-testid="cancel-integration-name-button"
                  />
                </div>
              )}
              {!isIntegrationNameInEditMode && (
                <div className="mt-1 flex flex-wrap">
                  <div>
                    <Tag
                      key={integrationProperties.INTEGRATION_NAME}
                      className="tag-selected-item"
                    >
                      {integrationProperties.INTEGRATION_NAME}
                    </Tag>
                  </div>

                  <Button
                    size="small"
                    className="bg-ciq-neutral-25"
                    onClick={() => {
                      setIsIntegrationNameInEditMode(true);
                    }}
                    icon={<EditOutlined />}
                    disabled={!canChangeIntegration || updateInProgress}
                    title={
                      !canChangeIntegration
                        ? ErrorMessages.PermissionNotGranted
                        : ""
                    }
                    data-testid="edit-integration-name-button"
                  />
                </div>
              )}
            </div>
            <div>
              <div className="flex">
                <span className="form-field-heading-2">ACCOUNT NAME</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-y-2">
                <Tag key={0} className="tag-selected-item">
                  {subscriptionIntegrationProperties.PROCORE_COMPANY_NAME}
                </Tag>
              </div>
            </div>
            <div>
              <div className="flex">
                <span className="form-field-heading-2">PROJECT NAME</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-y-2">
                <Tag key={0} className="tag-selected-item">
                  {integrationProperties.PROCORE_PROJECT_NAME}
                </Tag>
              </div>
            </div>
            <div>
              <div className="flex">
                <span className="form-field-heading-2">
                  Notification Recipients
                </span>
                <Popover
                  content={
                    <div className="text-sm font-medium">
                      Enter the email address of users who need to be notified
                      in case of failure in authorization.
                    </div>
                  }
                >
                  <InfoCircleOutlined className="pl-0.5 text-xs mt-0.5" />
                </Popover>
              </div>
              {isEmailInEditMode && (
                <div className="w-full flex items-start space-x-2">
                  <div className="w-full">
                    <Select
                      className="normal-case w-full"
                      mode="tags"
                      placeholder="Email IDs"
                      notFoundContent={null}
                      dropdownStyle={{ display: "none" }}
                      value={alertEmails}
                      onChange={(value: any) => {
                        setAlertEmails(value);
                      }}
                      disabled={savingEmailInProg || updateInProgress}
                      data-testid="alert-email-select"
                    />
                    {alertEmails.length === 0 && (
                      <div className="text-ciq-error-500">
                        Please enter at least one email
                      </div>
                    )}
                  </div>
                  <Button
                    size="small"
                    className="bg-ciq-neutral-25 p-2 mt-1"
                    title="Save"
                    icon={<SaveOutlined />}
                    onClick={saveEmail}
                    disabled={
                      alertEmails.length === 0 ||
                      savingEmailInProg ||
                      updateInProgress
                    }
                    loading={savingEmailInProg}
                    data-testid="save-alert-emails-button"
                  />
                  <Button
                    size="small"
                    className="bg-ciq-neutral-25 mt-1"
                    title="Cancel"
                    onClick={resetEmailFieldState}
                    icon={<CloseOutlined />}
                    disabled={savingEmailInProg}
                    data-testid="cancel-alert-emails-button"
                  />
                </div>
              )}
              {!isEmailInEditMode && (
                <div className="mt-1 flex flex-wrap gap-y-2">
                  {alertEmails.map((email: string) => {
                    return (
                      <Tag key={email} className="tag-selected-item">
                        {email}
                      </Tag>
                    );
                  })}

                  <Button
                    size="small"
                    className="bg-ciq-neutral-25"
                    onClick={() => {
                      setIsEmailInEditMode(true);
                    }}
                    icon={<EditOutlined />}
                    disabled={!canChangeIntegration || updateInProgress}
                    title={
                      !canChangeIntegration
                        ? ErrorMessages.PermissionNotGranted
                        : ""
                    }
                    data-testid="edit-alert-emails-button"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Divider className="mx-0 pb-0 mb-0" />
      <footer className="p-6 flex justify-end gap-x-4">
        <Button
          size="middle"
          onClick={() => {
            dispatch({
              type: "SET_ACTIVE_LIST_VIEW"
            });
          }}
          data-testid="back-button"
        >
          Back
        </Button>
      </footer>
    </div>
  );
}
